import React, { useCallback, useMemo } from "react";

import { EditableCard } from "@amphore-dev/amphore-lib";
import { t } from "i18next";

import { TUserSettings } from "@/types";

import { RHYTHM_FIELDS, RHYTHM_VALIDATION } from "./RhythmCard.fields";

export interface IRhythmCardProps {
	settings: TUserSettings;
	canEdit: boolean;
	onSave: (settings: TUserSettings) => Promise<unknown>;
	className?: string;
}

/**
 * My working rhythm, on the profile: one chip per day, the daily hours
 * and the weekly line. Edit, in a modal (the card's inline mode has no
 * submit footer): the per-day switch and hours, and how long a billed
 * day is. It belongs to the person, not to a view - the same rhythm
 * whatever they work on.
 */
export const RhythmCard: React.FC<IRhythmCardProps> = ({
	settings,
	canEdit,
	onSave,
	className,
}) => {
	// A function: the days editor needs the daily default as typed in the
	// form, not the saved one.
	const fields = useCallback(
		(ctx: { values: Partial<TUserSettings> }) =>
			RHYTHM_FIELDS(ctx.values.hoursPerDay ?? undefined),
		[]
	);
	const validationSchema = useMemo(() => RHYTHM_VALIDATION(), []);

	return (
		<div data-testid="rhythm-card" className={className}>
			<EditableCard
				title={t("account.rhythm.title")}
				fields={fields as never}
				values={settings}
				validationSchema={validationSchema}
				onSubmit={async (values) => {
					await onSave(values as TUserSettings);
				}}
				editInModal
				formikWrapper
				readOnly={!canEdit}
				columns={3}
				displayRequiredAsterisk={false}
				editColumns={2}
				editLabel={t("actions.edit")}
				submitLabel={t("actions.save")}
				cancelLabel={t("actions.cancel")}
			/>
		</div>
	);
};
